import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';

import { mensagemDeErro } from '@/lib/erros';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Dialog } from '@/ui/primitives/dialog';
import { Campo, Input } from '@/ui/primitives/input';
import { Select } from '@/ui/primitives/select';
import {
  type CentroCusto,
  type CriarCentroCustoRequest,
  criarCentroCustoRequestSchema,
} from '@fiscoai/contracts';

import { useAtualizarCentroCusto, useCriarCentroCusto } from './api';
import { OPCOES_NATUREZA } from './rotulos';

interface Props {
  empresaId: string;
  /** Nulo: novo; senão edita (o código não muda — é a identidade na planilha). */
  editando: CentroCusto | null;
  aberto: boolean;
  aoFechar: () => void;
}

/** Novo centro de custo ou edição de descrição e natureza. */
export function DialogoCentroCusto({ empresaId, editando, aberto, aoFechar }: Props) {
  const criar = useCriarCentroCusto(empresaId);
  const atualizar = useAtualizarCentroCusto(empresaId);
  const form = useForm<CriarCentroCustoRequest>({
    resolver: zodResolver(criarCentroCustoRequestSchema),
    values: editando
      ? { codigo: editando.codigo, descricao: editando.descricao, natureza: editando.natureza }
      : { codigo: '', descricao: '', natureza: 'DESPESA' },
  });
  const erros = form.formState.errors;
  const fechar = () => {
    form.reset();
    aoFechar();
  };

  const enviar = form.handleSubmit(async (dados) => {
    try {
      if (editando) {
        await atualizar.mutateAsync({
          id: editando.id,
          descricao: dados.descricao,
          natureza: dados.natureza,
        });
        toast.success(
          editando.natureza !== dados.natureza && editando.usos > 0
            ? 'Centro de custo salvo — as notas que o usam serão reconferidas'
            : 'Centro de custo salvo',
        );
      } else {
        await criar.mutateAsync(dados);
        toast.success('Centro de custo cadastrado');
      }
      fechar();
    } catch (erro) {
      toast.error(mensagemDeErro(erro));
    }
  });

  return (
    <Dialog
      aberto={aberto}
      aoMudar={(abrir) => {
        if (!abrir) fechar();
      }}
      titulo={editando ? `Centro de custo ${editando.codigo}` : 'Novo centro de custo'}
      descricao="A natureza decide o grupo contábil dos itens de uso e consumo e de serviço."
      acoes={
        <>
          <Button variante="fantasma" onClick={fechar}>
            Cancelar
          </Button>
          <Button
            variante="primario"
            onClick={() => void enviar()}
            carregando={criar.isPending || atualizar.isPending}
          >
            {editando ? 'Salvar' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4 sm:grid-cols-[10rem_1fr]"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
      >
        <Campo rotulo="Código" erro={erros.codigo?.message}>
          {(a) => (
            <Input
              {...a}
              {...form.register('codigo')}
              className="font-mono uppercase"
              disabled={editando !== null}
              autoFocus={editando === null}
            />
          )}
        </Campo>
        <Campo rotulo="Descrição" erro={erros.descricao?.message}>
          {(a) => <Input {...a} {...form.register('descricao')} autoFocus={editando !== null} />}
        </Campo>
        <Campo rotulo="Natureza" className="sm:col-span-2" erro={erros.natureza?.message}>
          {(a) => (
            <Controller
              control={form.control}
              name="natureza"
              render={({ field }) => (
                <Select
                  {...a}
                  opcoes={OPCOES_NATUREZA}
                  valor={field.value}
                  aoMudar={(v) => {
                    field.onChange(v);
                  }}
                />
              )}
            />
          )}
        </Campo>
      </form>
    </Dialog>
  );
}
